import React, { useMemo } from 'react';
import {
  AvailableAppointmentTimeString,
  groupAllTimesTeam,
  groupAvailableTimesTeam,
  TeamTimeslotByDate,
} from '../utils/helpers';
import { ErrorSource, RawError } from '@datadog/browser-core';
import { datadogRum } from '@datadog/browser-rum';
import { DateTime } from 'luxon';
import { TimezoneContext } from '../contexts/TimezoneContext';

interface SetAvailableTimeslotsByDateProps {
  showUnavailable: boolean;
  teamRawAvailTimeslots?: AvailableAppointmentTimeString[];
  selectedTimezoneValue: Record<'label' | 'value', string>;
  isSelf?: boolean;
  hasApplied?: boolean;
  isCustomerTimezoneActive?: boolean;
}

//This hook is used to group all available timeslots by date depending on the selected timezone and showUnavailable flag.
export function useAvailableTimeslotsByDate({
  showUnavailable,
  teamRawAvailTimeslots,
  selectedTimezoneValue,
  isSelf = false,
  isCustomerTimezoneActive,
  hasApplied,
}: SetAvailableTimeslotsByDateProps) {
  const brokerTimezone = DateTime.local().zoneName;
  const { timezone } = React.useContext(TimezoneContext);

  const allAvailableTimeslotsByDate: TeamTimeslotByDate | undefined = useMemo(() => {
    if (!hasApplied) return undefined;
    try {
      if (showUnavailable) {
        const timezoneToUse = !isSelf
          ? selectedTimezoneValue.value
          : isCustomerTimezoneActive
          ? timezone
          : brokerTimezone;
        return groupAllTimesTeam(teamRawAvailTimeslots, timezoneToUse);
      } else if (teamRawAvailTimeslots && teamRawAvailTimeslots.length > 0) {
        const timezoneToUse = !isSelf
          ? selectedTimezoneValue.value
          : isCustomerTimezoneActive
          ? timezone
          : brokerTimezone;
        return groupAvailableTimesTeam(teamRawAvailTimeslots, timezoneToUse);
      } else {
        return undefined;
      }
    } catch (error) {
      (error as unknown as RawError).source = ErrorSource.SOURCE;
      datadogRum.addError(error);
      return undefined;
    }
  }, [
    teamRawAvailTimeslots,
    selectedTimezoneValue.value,
    showUnavailable,
    brokerTimezone,
    timezone,
    hasApplied,
    isCustomerTimezoneActive,
    isSelf,
  ]);

  return allAvailableTimeslotsByDate;
}

export default useAvailableTimeslotsByDate;
